import React, { Fragment } from "react";
import * as Sentry from "@sentry/browser";
import {
  Activity,
  Customer,
  CustomerTransaction,
  Entitlement,
  Scheme,
  Subscription,
  Ticket,
  User,
  UssdAccount,
  VirtualAccountNumber,
} from "../helpers/shared-interfaces";
import { NavLink, RouteComponentProps } from "react-router-dom";
import {
  capitalizeFirstLetter,
  formatSubscriptionTags,
  formatter,
  mapSubscriptionToContribution,
  request,
} from "../helpers/utilities";
import DescriptionsItem from "antd/lib/descriptions/Item";
import {
  GET_CUSTOMER_INTERACTIONS_QUERY,
  GET_CUSTOMER_ONLINE_TRANSACTIONS,
  GET_CUSTOMER_QUERY,
  GET_CUSTOMER_SCHEMES,
  GET_CUSTOMER_SUBSCRIPTIONS,
  GET_CUSTOMER_TICKETS,
} from "../gql/customers";
import { AxiosResponse } from "axios";
import {
  Col,
  Collapse,
  Descriptions,
  Divider,
  Row,
  Spin,
  Table,
  Tag,
  Card,
} from "antd";
import CustomerAssociationsView from "../components/CustomerAssociationsView";
import ContactDetailsView from "../components/ContactDetailsView";
import PaymentAccountsView from "../components/PaymentsAccountsView";
import CustomerBeneficiariesView from "../components/CustomerBeneficiariesView";
import { ticketColumns, transferRequestsColumns } from "./ViewCompany";
import {
  ContributionsTableFactory,
  RedemptionsRequestsTableFactory,
  customerBalanceSummaryColumn,
  generateColumns,
  onlineTransactionColumns,
} from "./ViewCustomer";
import TagsSection from "../components/TagsSection";

const { Panel } = Collapse;

interface RouteParams {
  id: string;
}

interface Props
  extends RouteComponentProps<
    RouteParams,
    any,
    { type: string; requests?: Customer[] }
  > {
  entitlement: Entitlement;
  user: User;
  activityId?: string;
}

interface State {
  id: string | number;
  isLoading: boolean;
  tickets: Ticket[];
  phoenixTickets: Ticket[];
  ussdAccount: UssdAccount | undefined;
  customerInteractions: Activity[];
  currentCustomerToProcess: number;
  customer?: Customer;
  currentCustomerVan: VirtualAccountNumber | undefined;
  currentFundCode: string;
  areInteractionsLoading: boolean;
  activitiesPage: number;
  activitiesCount: number;
  customerTransactions: CustomerTransaction[];
  hasLoadedContributions: boolean;
  customerBalanceSummary: Scheme[];
  contributionsLoader: boolean;
}

export const ArchivedDetails = (props: {
  customer: Customer;
  phoenixTickets?: Ticket[];
  customerBalanceSummary: Scheme[];
  customerTransactions: CustomerTransaction[];
  contributionsLoader: boolean;
  getProductContribution: (key: string | string[]) => void;

  extra?: JSX.Element;
}): JSX.Element => {
  const segment = props.customer.customerSegment?.toLowerCase();
  const mapSegmentToClass: Record<string, string> = {
    silver: "#757575",
    diamond: "#81c784",
    platinum: "#000000",
    bronze: "#ffeb3b",
  };
  const segmentColor = segment ? mapSegmentToClass[segment] : "";

  const hasBeneficiary =
    props.customer.customerSubscriptions &&
    props.customer.customerSubscriptions.some(
      (subscription) => subscription.customerBeneficiaries.length > 0,
    );

  const subscriptionWithBeneficiary =
    props.customer.customerSubscriptions?.filter(
      (subscription) => subscription.customerBeneficiaries.length > 0,
    );

  const hasSubscription =
    props.customer.customerSubscriptions &&
    props.customer.customerSubscriptions.length > 0;

  const subscriptionsWithContributions = mapSubscriptionToContribution(
    [],
    props.customer.customerSubscriptions,
    props.customerBalanceSummary,
  );

  return (
    <Fragment>
      <Descriptions
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <div style={{ marginRight: "50px" }}>Bio Information</div>
          </div>
        }
      >
        {props.customer.petraId && (
          <Descriptions.Item label={<b>Petra ID</b>} key="Petra ID">
            {props.customer.petraId}
          </Descriptions.Item>
        )}

        {props.customer.firstName && (
          <Descriptions.Item label={<b>Customer Name</b>} key="Customer Name">
            {props.customer.fullname}
          </Descriptions.Item>
        )}

        {props.customer.ssnitNumber && (
          <Descriptions.Item label={<b>SSNIT Number</b>} key="ssnitNumber">
            {props.customer.ssnitNumber}
          </Descriptions.Item>
        )}
        {props.customer.dateOfBirth && (
          <Descriptions.Item label={<b>Date of Birth</b>} key="dateOfBirth">
            {props.customer.dateOfBirth}
          </Descriptions.Item>
        )}
        {props.customer.maritalStatus && (
          <Descriptions.Item label={<b>Marital Status</b>} key="Marital Status">
            {capitalizeFirstLetter(props.customer.maritalStatus.name)}
          </Descriptions.Item>
        )}

        {props.customer.country && (
          <Descriptions.Item label={<b>Country</b>} key="Country">
            {capitalizeFirstLetter(props.customer.country.name)}
          </Descriptions.Item>
        )}

        {props.customer.gender && (
          <Descriptions.Item label={<b>Gender</b>} key="Gender">
            {capitalizeFirstLetter(props.customer.gender)}
          </Descriptions.Item>
        )}

        {props.customer.idType && (
          <Descriptions.Item label={<b>ID Type</b>} key="ID Type">
            {props.customer.idType.name}
          </Descriptions.Item>
        )}

        {props.customer.photoIdNumber && (
          <Descriptions.Item label={<b>ID Number</b>} key="ID Number">
            {capitalizeFirstLetter(props.customer.photoIdNumber)}
          </Descriptions.Item>
        )}

        {props.customer.ghanaCardNo && (
          <Descriptions.Item
            label={<b>Ghana Card ID Number</b>}
            key="Ghana Card ID Number"
          >
            {capitalizeFirstLetter(props.customer.ghanaCardNo)}
          </Descriptions.Item>
        )}

        {props.customer.ghanaCardIdUrl && (
          <Descriptions.Item label={<b>Ghana Card ID</b>} key="Ghana Card ID">
            {
              <a
                target="_blank"
                href={props.customer.ghanaCardIdUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            }
          </Descriptions.Item>
        )}

        {props.customer.customerSegment && (
          <Descriptions.Item label={<b>Segment</b>} key="Category">
            <Tag color={segmentColor}>
              {capitalizeFirstLetter(props.customer.customerSegment)}
            </Tag>
          </Descriptions.Item>
        )}

        {props.customer.photoIdUrl && (
          <Descriptions.Item label={<b>ID</b>} key="ID">
            {
              <a
                target="_blank"
                href={props.customer.photoIdUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            }
          </Descriptions.Item>
        )}

        {props.customer.signatureUrl && (
          <Descriptions.Item
            label={<b>Customer Signature</b>}
            key="Customer Signature"
          >
            {
              <a
                target="_blank"
                href={props.customer.signatureUrl}
                rel="noopener noreferrer"
              >
                Click To Open Image
              </a>
            }
          </Descriptions.Item>
        )}

        {props.customer?.mergedTo && (
          <Descriptions.Item label={<b>Merged To</b>} key="customerMergeLogs">
            <NavLink
              to={`/customers/${props.customer.mergedTo.id}`}
              key={props.customer.mergedTo.id}
            >
              {props.customer.mergedTo.fullname}
            </NavLink>
          </Descriptions.Item>
        )}

      </Descriptions>

      <ContactDetailsView
        phones={props.customer.activeCustomerPhones}
        emails={props.customer.customerEmails}
        adresses={props.customer.customerAddresses}
        formerEmployees={{ data: [], totalCount: 0 }}
        currentEmployees={{ data: [], totalCount: 0 }}
        style={{ marginBottom: "30px", marginTop: "30px" }}
      />
      {(props.customer.employers.length > 0 ||
        props.customer.formerCompanies.length > 0 ||
        props.customer.customerCompanyAssociations.length > 0) && (
        <CustomerAssociationsView
          style={{
            marginBottom: "25px",
          }}
          employers={
            props.customer.employers ??
            props.customer.customerCompanyAssociations.map(
              (association) => association.company,
            )
          }
          formerCompanies={props.customer.formerCompanies}
          companyAssociations={props.customer.customerCompanyAssociations}
        />
      )}

      {(props.customer.customerDebitAccounts.length > 0 ||
        props.customer.customerPayoutAccounts.length > 0) && (
        <PaymentAccountsView
          style={{
            marginBottom: "25px",
          }}
          payoutAccounts={props.customer.customerPayoutAccounts}
          debitAccounts={props.customer.customerDebitAccounts}
        />
      )}

      {hasBeneficiary && (
        <CustomerBeneficiariesView
          style={{ marginTop: "30px" }}
          subscriptions={subscriptionWithBeneficiary as Subscription[]}
        />
      )}

      <div
        style={{
          border: "1px solid rgb(232 232 232)",
          padding: "20px",
          boxSizing: "border-box",
          marginTop: "30px",
        }}
      >
        <Spin spinning={props.contributionsLoader}>
          <Descriptions title="Contributions" />
          <Collapse
            accordion
            style={{
              marginBottom: "12px",
            }}
            onChange={props.getProductContribution}
          >
            <Panel header="Total Balance Summary" key="totalBalanceSummary">
              <Table
                dataSource={props.customerBalanceSummary}
                columns={customerBalanceSummaryColumn}
                bordered
              />
            </Panel>
            {hasSubscription &&
              subscriptionsWithContributions.map((item) => {
                const payountInfo = props.customer.customerSubscriptions?.find(
                  (subItem) =>
                    subItem.product.id === item.subscription.product.id,
                )?.customerPayoutAccounts?.[0];
                const productDuration =
                  props.customer.customerSubscriptions?.find(
                    (subItem) =>
                      subItem.product.id === item.subscription.product.id,
                  )?.productDuration;
                return (
                  <Panel
                    header={item.subscription.product.name}
                    key={item.subscription.product.fundCode as string}
                  >
                    <Row>
                      <Col span={16}>
                        <Descriptions>
                          <DescriptionsItem label="Balance">
                            {formatter.format(item.scheme.balance)}
                          </DescriptionsItem>
                          <DescriptionsItem label="Redeemable Balance">
                            {formatter.format(item.scheme.redeemableBalance)}
                          </DescriptionsItem>
                        </Descriptions>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={16}>
                        <Descriptions>
                          <DescriptionsItem label="Total Contributions">
                            {formatter.format(item.scheme.balance)}
                          </DescriptionsItem>
                          <DescriptionsItem label="Total Redemptions">
                            {formatter.format(item.scheme.redemptions)}
                          </DescriptionsItem>
                        </Descriptions>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={8}>
                        <Descriptions>
                          <DescriptionsItem label="Product Duration">
                            {productDuration ?? "n/a"}
                          </DescriptionsItem>
                        </Descriptions>
                      </Col>
                    </Row>
                    <Row>
                      <Col span={24}>
                        <Descriptions>
                          <DescriptionsItem label="Bank">
                            {payountInfo?.bankBranch?.name ?? "n/a"}
                          </DescriptionsItem>
                          <DescriptionsItem label="Bank Account Name">
                            {payountInfo?.name ?? "n/a"}
                          </DescriptionsItem>
                          <DescriptionsItem label="Bank Account Number">
                            {payountInfo?.accountNumber ?? "n/a"}
                          </DescriptionsItem>
                        </Descriptions>
                      </Col>
                    </Row>

                    {item.subscription.customerSubscriptionTags.length > 0 && (
                      <Row style={{ marginTop: "10px", marginBottom: "10px" }}>
                        <TagsSection
                          tags={formatSubscriptionTags(
                            item.subscription.customerSubscriptionTags,
                          )}
                        />
                      </Row>
                    )}

                    <Divider />
                    {ContributionsTableFactory(
                      item.scheme.contributions,
                      generateColumns(item.scheme),
                    )}
                  </Panel>
                );
              })}
          </Collapse>
        </Spin>
      </div>

      <div
        style={{
          border: "1px solid rgb(232 232 232)",
          padding: "20px",
          boxSizing: "border-box",
          marginTop: "30px",
        }}
      >
        <Descriptions title={<div>Requests</div>}></Descriptions>
        <Collapse
          style={{
            marginBottom: "12px",
          }}
        >
          {props.customer.redemptionRequests?.length > 0 && (
            <Panel key="redemptionRequests" header="Redemption Requests">
              {RedemptionsRequestsTableFactory(
                props.customer.redemptionRequests,
              )}
            </Panel>
          )}

          {props.customer.transferRequests?.length > 0 && (
            <Panel key="transferRequests" header="Transfer Requests">
              <Table
                dataSource={props.customer.transferRequests}
                columns={transferRequestsColumns}
                bordered
              />
            </Panel>
          )}
          {props.customerTransactions?.length > 0 && (
            <Panel key="customerTransactions" header="Online Transactions">
              <Table
                dataSource={props.customerTransactions}
                columns={onlineTransactionColumns}
                bordered
              />
            </Panel>
          )}
        </Collapse>
      </div>

      <div
        style={{
          border: "1px solid rgb(232 232 232)",
          padding: "20px",
          boxSizing: "border-box",
          marginTop: "30px",
        }}
      >
        <Descriptions title={<div>Phoenix Tickets</div>}></Descriptions>

        <Collapse
          style={{
            marginBottom: "12px",
          }}
        >
          {props?.phoenixTickets && props?.phoenixTickets?.length > 0 && (
            <Panel key="phoenixTickets" header="Phoenix Tickets">
              <Table
                dataSource={props.phoenixTickets}
                columns={ticketColumns}
                bordered
              />
            </Panel>
          )}
        </Collapse>
      </div>
    </Fragment>
  );
};

export default class ViewArchivedCustomer extends React.Component<
  Props,
  State
> {
  constructor(props: Readonly<Props>) {
    super(props);
    this.state = {
      isLoading: true,
      id: this.props.match.params.id,
      customerInteractions: [],
      tickets: [],
      ussdAccount: undefined,
      phoenixTickets: [],
      currentCustomerToProcess: 0,
      customer: undefined,
      currentFundCode: "",
      currentCustomerVan: undefined,
      customerTransactions: [],
      activitiesCount: 0,
      activitiesPage: 1,
      areInteractionsLoading: false,
      hasLoadedContributions: false,
      customerBalanceSummary: [],
      contributionsLoader: false,
    };
  }

  async componentDidMount(): Promise<void> {
    this.setState(
      {
        id: this.props.match.params.id,
        currentCustomerToProcess:
          this.props?.location?.state?.requests?.findIndex(
            (item) =>
              item?.id.toString() === this.props.match.params.id.toString(),
          ) ?? 0,
      },
      () => {
        this.getCustomer();
        this.getCustomerOnlineTransactions();
        this.getCustomerInteractions();
        this.getPhoenixTickets();
      },
    );
  }

  getPhoenixTickets = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_TICKETS,
      variables: { customerId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          customer: Customer;
        };
      }>): void => {
        if (data.data.customer) {
          this.setState({
            phoenixTickets: data.data?.customer?.tickets ?? [],
            isLoading: false,
          });
        }
      },
    );
  };

  getCustomerInteractions = (): void => {
    const { id: customerId, activitiesPage } = this.state;
    this.setState({ areInteractionsLoading: true });
    request({
      query: GET_CUSTOMER_INTERACTIONS_QUERY,
      variables: { customerId, page: activitiesPage.toString() },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            interactionsForCustomer: {
              count: number;
              results: Activity[];
            };
          };
        }>): void => {
          this.setState({
            customerInteractions: data.data.interactionsForCustomer.results,
            activitiesCount: data.data.interactionsForCustomer.count,
          });
        },
      )
      .finally((): void => {
        this.setState({ areInteractionsLoading: false });
      });
  };

  getCustomerOnlineTransactions = (): void => {
    const { id: customerId } = this.state;
    interface Response {
      data: {
        customerTransactions: CustomerTransaction[];
      };
    }
    request({
      query: GET_CUSTOMER_ONLINE_TRANSACTIONS,
      variables: { customerId },
    }).then(({ data }: AxiosResponse<Response>): void => {
      this.setState({ customerTransactions: data.data.customerTransactions });
    });
  };

  getCustomerSubscriptions = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_SUBSCRIPTIONS,
      variables: { customerId },
    })
      .then(
        ({
          data,
        }: AxiosResponse<{
          data: {
            customer: Customer;
          };
        }>): void => {
          this.setState({
            customer: {
              ...this.state.customer,
              ...data.data.customer,
            },
          });
        },
      )
      .finally((): void => {
        this.setState({
          isLoading: false,
        });
      });
  };

  getCustomer = (): void => {
    const { id: customerId } = this.state;
    this.setState({ isLoading: true });
    request({
      query: GET_CUSTOMER_QUERY,
      variables: { customerId },
    }).then(
      ({
        data,
      }: AxiosResponse<{
        data: {
          customer: Customer;
        };
      }>): void => {
        if (data.data.customer) {
          this.setState({
            customer: data.data.customer,
            isLoading: false,
            ussdAccount: data.data.customer.ussdAccount,
          });
        } else {
          this.redirectToAllArchivedCustomersPage();
        }
      },
    );
  };

  redirectToAllArchivedCustomersPage = (): void => {
    this.props.history.push("/archived-customers");
  };

  getContributionData = async (key: string | string[]): Promise<void> => {
    await this.getProductContributionSummary();
  };

  getProductContributionSummary = async () => {
    interface Response {
      data: {
        getAllSchemesExtended: Scheme[];
      };
    }

    const { hasLoadedContributions } = this.state;
    if (hasLoadedContributions) return;

    this.setState({ contributionsLoader: true });

    const customer = this.state.customer;

    try {
      const response: AxiosResponse<Response> = await request({
        query: GET_CUSTOMER_SCHEMES,
        variables: { customerPetraId: customer?.petraId },
      });

      this.setState({
        customerBalanceSummary: response.data.data.getAllSchemesExtended,
      });
      this.getCustomerSubscriptions();
    } catch (error) {
      Sentry.captureException(error);
    }

    this.setState({
      contributionsLoader: false,
      hasLoadedContributions: true,
    });
  };

  render(): JSX.Element {
    const {
      isLoading,
      customer,
      phoenixTickets,
      customerBalanceSummary,
      customerTransactions,
    } = this.state;
    let userInterface: JSX.Element;

    if (customer) {
      userInterface = (
        <ArchivedDetails
          customer={customer}
          phoenixTickets={phoenixTickets}
          customerBalanceSummary={customerBalanceSummary}
          customerTransactions={customerTransactions}
          contributionsLoader={this.state.contributionsLoader}
          getProductContribution={this.getContributionData}
        />
      );
    } else {
      userInterface = <Fragment />;
    }

    return (
      <Spin spinning={isLoading}>
        <Row>
          <Card
            style={{
              overflow: "hidden",
            }}
          >
            <Row
              style={{
                marginBottom: "20px",
              }}
            >
              <Col span={24}>{userInterface}</Col>
            </Row>
          </Card>
        </Row>
      </Spin>
    );
  }
}
